import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { Link as RouterLink } from "react-router-dom";
import { colors } from "../constants/designTokens";

const noticeSeverity = {
  attention: "warning",
  tip: "info",
  important: "success",
};

export default function HelpSection({ section, expanded, onChange }) {
  return (
    <Accordion
      id={section.id}
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{
        scrollMarginTop: 20,
        m: 0,
        mb: 0,
        border: `1px solid ${colors.border}`,
        borderRadius: "8px !important",
        bgcolor: colors.surface,
        overflow: "hidden",
        "&::before": { display: "none" },
        "&.Mui-expanded": { m: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded />}
        aria-controls={`${section.id}-content`}
        id={`${section.id}-header`}
        sx={{
          minHeight: 76,
          px: { xs: 2, md: 2.5 },
          "&.Mui-expanded": { minHeight: 76 },
          "& .MuiAccordionSummary-content": { my: 1.8 },
          "& .MuiAccordionSummary-content.Mui-expanded": { my: 1.8 },
        }}
      >
        <Box>
          <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 17 }}>
            {section.title}
          </Typography>
          <Typography sx={{ color: colors.muted, fontSize: 13.5, mt: 0.25 }}>
            {section.summary}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pt: 0, pb: 3 }}>
        <Divider sx={{ mb: 2.25 }} />
        <Stack spacing={2.25}>
          {section.steps?.length ? (
            <Box>
              <Typography sx={{ color: colors.text, fontWeight: 800, mb: 1.25 }}>
                Passo a passo
              </Typography>
              <Stack component="ol" spacing={1.15} sx={{ m: 0, pl: 3 }}>
                {section.steps.map((step) => (
                  <Typography
                    component="li"
                    key={step}
                    sx={{ color: colors.muted, fontSize: 14, lineHeight: 1.6, pl: 0.5 }}
                  >
                    {step}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}

          {section.details?.length ? (
            <Stack spacing={1.6}>
              {section.details.map((detail) => (
                <Box key={detail.title}>
                  <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 14.5 }}>
                    {detail.title}
                  </Typography>
                  <Typography sx={{ color: colors.muted, fontSize: 14, lineHeight: 1.65, mt: 0.35 }}>
                    {detail.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}

          {section.glossary?.length ? (
            <Stack spacing={0} divider={<Divider flexItem />}>
              {section.glossary.map(([term, description]) => (
                <Box
                  key={term}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
                    gap: { xs: 0.35, sm: 2 },
                    py: 1.2,
                  }}
                >
                  <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 14 }}>
                    {term}
                  </Typography>
                  <Typography sx={{ color: colors.muted, fontSize: 14, lineHeight: 1.55 }}>
                    {description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}

          {section.notice ? (
            <Alert severity={noticeSeverity[section.notice.severity] || "info"}>
              {section.notice.text}
            </Alert>
          ) : null}

          {section.actions?.length ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {section.actions.map((action) => (
                <Button
                  key={action.to}
                  component={RouterLink}
                  to={action.to}
                  variant="outlined"
                  endIcon={<OpenInNewRounded />}
                  sx={{ textTransform: "none", fontWeight: 800, alignSelf: { xs: "stretch", sm: "flex-start" } }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
